import React from "react";

import { flexRender } from "@tanstack/react-table";
import { Captions, Globe } from "lucide-react";
import moment from "moment";
import { renderToStaticMarkup } from "react-dom/server";

import TooltipLines from "@/src/components/tooltip/TooltipLines";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/src/components/ui/avatar";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { cn } from "@/src/lib/utils";
import { DURATION_UNITS } from "@/src/utils/durations";
import { getNameInitials } from "@/src/utils/initials";

import AdvancedRightsLink from "./AdvancedRightsLink";
import { MEDIA_PUBLICATION_STATUS } from "../../../constants/media";
import { restrictionsColumn } from "../../common/restrictionsColumn";
import Tag from "../../common/tools/Tag";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  countColumn,
  dateColumn,
  durationColumn,
  lazyActionsColumn,
  statusColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { CELL_ICON_CLASS } from "../../dataTable/dataTableStyles";
import { contentPath, CONTENTS_ROUTE } from "../../routes/routes";
import ContentTypeIcon from "../ContentTypeIcon";
import { CONTENT_TYPE_ICONS } from "../contentTypeIcons";

import type { TFunction } from "i18next";

import type { ContentListRow, MaybeOwner } from "./contentColumns.types";
import type { IndexMedia } from "../../../api/MediaAPI.types";
import type {
  DataTableColumnDef,
  RowAction,
} from "../../dataTable/dataTable.types";

// L'API renvoie `active`/`stop` mais filtre sur `enabled`/`disabled`.
const PUBLICATION_STATUS_FILTER_VALUES: Partial<Record<string, string>> = {
  [MEDIA_PUBLICATION_STATUS.ACTIVE]: "enabled",
  [MEDIA_PUBLICATION_STATUS.STOP]: "disabled",
};

export function getPubStatus(content: IndexMedia): string {
  if (content.status === "archived") return "archived";
  return (
    PUBLICATION_STATUS_FILTER_VALUES[content.publication_status] ??
    content.publication_status
  );
}

interface ContentColumnsOptions {
  t: TFunction;
  contentType: string;
  isLive: boolean;
  isHosted: boolean;
  hasTypeColumn: boolean;
  hasCuepoints: boolean;
  ownerRole: boolean;
  loadActions: (row: ContentListRow) => Promise<RowAction[]>;
}

const LIVE_START_FORMAT = "DD/MM/YYYY HH:mm";
const ONGOING_LIVE_STATUSES = new Set(["ongoing", "live"]);

export function getContentHref(contentType: string, uuid: string): string {
  return contentPath(contentType, uuid) ?? CONTENTS_ROUTE;
}

function getContentTypeTooltip(content: ContentListRow): string {
  if (content.is_replay) return "replay";
  if (content.type === "master") return "master_video";
  return `tooltip_${content.content_type}`;
}

function getOwnerFullName(owner: MaybeOwner): string {
  return [owner?.first_name, owner?.last_name].filter(Boolean).join(" ");
}

export function contentColumns({
  t,
  contentType,
  isLive,
  isHosted,
  hasTypeColumn,
  hasCuepoints,
  ownerRole,
  loadActions,
}: ContentColumnsOptions): DataTableColumnDef<ContentListRow>[] {
  const pauseLabel = t("table:status.pause");
  const restrictions = restrictionsColumn<ContentListRow>({
    t,
    layout: { width: "8%", minWidth: 100 },
    showsTemporalTooltip: true,
  });

  const typeColumnList: DataTableColumnDef<ContentListRow>[] = hasTypeColumn
    ? [
        {
          accessorKey: "content_type",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title={t("type")} />
          ),
          enableSorting: false,
          enableHiding: true,
          meta: {
            label: t("type"),
            width: "4%",
            minWidth: 55,
            align: "center",
            filterOptions: isHosted
              ? [
                  {
                    value: "video",
                    label: t("video"),
                    icon: <CONTENT_TYPE_ICONS.video />,
                  },
                  {
                    value: "replay",
                    label: t("replay"),
                    icon: <CONTENT_TYPE_ICONS.replay />,
                  },
                  {
                    value: "audio",
                    label: t("audio"),
                    icon: <CONTENT_TYPE_ICONS.audio />,
                  },
                  {
                    value: "master",
                    label: t("master_video"),
                    icon: <CONTENT_TYPE_ICONS.master />,
                  },
                ]
              : undefined,
          },
          cell: ({ row }) => (
            <div
              className="flex items-center justify-center"
              data-tooltip-content={t(getContentTypeTooltip(row.original))}
              data-tooltip-id={APP_TOOLTIP_ID}
            >
              <ContentTypeIcon content={row.original} />
            </div>
          ),
        },
      ]
    : [];

  const liveOrLanguageColumnList: DataTableColumnDef<ContentListRow>[] = isLive
    ? [
        {
          accessorKey: "live_start",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title={t("start_time")} />
          ),
          enableHiding: true,
          meta: { label: t("start_time"), width: "11%", minWidth: 150 },
          cell: ({ row }) =>
            row.original.live_start
              ? moment
                  .utc(row.original.live_start)
                  .local()
                  .format(LIVE_START_FORMAT)
              : "",
        },
      ]
    : [
        {
          ...countColumn<ContentListRow>(
            "trad_count",
            t("languages-management"),
            <Globe />,
            { width: "9%", minWidth: 120 },
          ),
          enableHiding: true,
        },
      ];

  const subtitleColumnList: DataTableColumnDef<ContentListRow>[] = isHosted
    ? [
        {
          accessorKey: "subtitles_count",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title={t("subtitles")} />
          ),
          enableHiding: true,
          meta: {
            label: t("subtitles"),
            width: "9%",
            minWidth: 120,
            align: "center",
          },
          cell: ({ row }) => {
            if (row.original.subtitles_count <= 0) return null;
            return row.original.type === "master" ? (
              <Captions
                className={cn(CELL_ICON_CLASS, "inline-block align-middle")}
              />
            ) : (
              row.original.subtitles_count
            );
          },
        },
      ]
    : [];

  const cuepointColumnList: DataTableColumnDef<ContentListRow>[] = hasCuepoints
    ? [
        {
          accessorKey: "total_cuepoints",
          header: ({ column }) => (
            <span
              data-tooltip-content={t("cuepointsTooltip")}
              data-tooltip-id={APP_TOOLTIP_ID}
            >
              <DataTableColumnHeader column={column} title={t("cuepoints")} />
            </span>
          ),
          enableHiding: true,
          meta: {
            label: t("cuepoints"),
            width: "9%",
            minWidth: 115,
            align: "center",
          },
          cell: ({ row }) =>
            row.original.total_cuepoints > 0
              ? `${row.original.enabled_cuepoints}/${row.original.total_cuepoints}`
              : null,
        },
      ]
    : [];

  const ownerColumnList: DataTableColumnDef<ContentListRow>[] = ownerRole
    ? [
        {
          id: "owner",
          accessorFn: (content) =>
            [
              getOwnerFullName(content.owner),
              content.owner?.company,
              content.owner?.email,
            ]
              .filter(Boolean)
              .join(" "),
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title={t("owner")} />
          ),
          enableHiding: true,
          meta: {
            label: t("owner"),
            width: "8%",
            minWidth: 105,
            align: "center",
          },
          cell: ({ row }) => {
            const owner: MaybeOwner = row.original.owner;
            const ownerTooltip = renderToStaticMarkup(
              <TooltipLines
                lines={[getOwnerFullName(owner), owner?.company, owner?.email]}
              />,
            );
            if (!ownerTooltip && !owner?.avatar_url) return null;

            return (
              <span
                className="inline-flex"
                data-tooltip-html={ownerTooltip || undefined}
                data-tooltip-id={APP_TOOLTIP_ID}
              >
                <Avatar size="sm">
                  <AvatarImage alt="" src={owner?.avatar_url} />
                  <AvatarFallback>
                    {getNameInitials(owner?.first_name, owner?.last_name)}
                  </AvatarFallback>
                </Avatar>
              </span>
            );
          },
        },
      ]
    : [];

  return [
    statusColumn<ContentListRow>(
      "pub_status",
      t("status"),
      [
        { value: "enabled", label: t("table:status.enabled"), tone: "success" },
        { value: "pause", label: pauseLabel, tone: "warning" },
        { value: "disabled", label: t("table:status.disabled"), tone: "error" },
        { value: "archived", label: t("common:archived"), tone: "neutral" },
      ],
      undefined,
      (content) =>
        content.pub_status === "pause" && content.publication_reasons?.length
          ? [
              `${pauseLabel} :`,
              ...content.publication_reasons.map((reason) =>
                t(`common:pause_reasons.${reason}`),
              ),
            ]
          : undefined,
    ),
    ...typeColumnList,
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("title")} />
      ),
      meta: { searchable: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {row.original.type === "live" &&
          ONGOING_LIVE_STATUSES.has(row.original.live_status ?? "") ? (
            <Tag item={row.original} type="live">
              {t("common:live")}
            </Tag>
          ) : null}
          <DataTableLinkCell
            href={getContentHref(contentType, row.original.uuid)}
          >
            {row.original.label}
          </DataTableLinkCell>
        </div>
      ),
    },
    {
      ...durationColumn<ContentListRow>(
        "duration",
        t("duration"),
        DURATION_UNITS.SECONDS,
        "fastDuration",
        { width: "8%", minWidth: 100 },
      ),
      enableHiding: true,
    },
    {
      ...restrictions,
      cell: (context) =>
        context.row.original.rights_mode === "advanced" ? (
          <AdvancedRightsLink
            to={`${getContentHref(contentType, context.row.original.uuid)}?tab=rights`}
          />
        ) : (
          // Un fragment : sans lui, flexRender renvoie un ReactNode et eslint --fix rend la cellule async.
          <React.Fragment>
            {flexRender(restrictions.cell, context)}
          </React.Fragment>
        ),
      enableHiding: true,
    },
    ...liveOrLanguageColumnList,
    ...subtitleColumnList,
    ...cuepointColumnList,
    ...ownerColumnList,
    {
      ...dateColumn<ContentListRow>("updated_at", t("update_date"), {
        width: "10%",
      }),
      enableHiding: true,
    },
    lazyActionsColumn<ContentListRow>(loadActions, (content) => content.label),
  ];
}
